import {
  Box,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  WidgetListRow,
} from "@salestrak/ui";

import { AgeLabel } from "#app/components/age/age-label";
import { EntityTag } from "#app/components/entity";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { issueHref } from "#app/lib/utils/entity-hrefs";
import { RowAction, RowLinkActions } from "../shared/row-link-actions";
import type { FollowUpQueueRow } from "./use-follow-up-list";

/**
 * The rail takes room only while it shows. The kit's `hoverReveal` fades
 * chrome in place — it keeps its footprint so nothing moves under a hover —
 * but reserving three buttons' width at every row's end would truncate a
 * long name a quarter of the card early for verbs that are hidden at rest
 * (Spencer: the name may truncate further for them, on hover). Width follows
 * the same reveals the fade does: hover, the touch arm, focus inside the
 * rail, an open tooltip. Height it never takes — the rail itself is a
 * zero-height line (`RowLinkActions`), so the clip here is on the x axis
 * alone: `overflow-x-clip`, not `hidden`, because `clip` on one axis leaves
 * the other visible for the buttons to overhang. A library gap, recorded as
 * one: a reveal that collapses its footprint.
 */
const RAIL_COLLAPSE =
  "max-w-0 overflow-x-clip group-hover/row:max-w-none group-hover/row:ms-2 group-data-armed/row:max-w-none group-data-armed/row:ms-2 focus-within:max-w-none focus-within:ms-2 has-[[data-popup-open]]:max-w-none has-[[data-popup-open]]:ms-2";

/** The gutter's sizers: one per shape of age string the queue holds, stacked
 *  invisible in the one grid cell so the cell takes the widest. */
const SIZER_CELL = "invisible col-start-1 row-start-1";

/** The visible count, in that same cell, against its right edge. */
const COUNT_CELL = "col-start-1 row-start-1 justify-self-end";

/** The verb the rail leads with — the row's own Update, before Recent's two
 *  link actions. */
const UPDATE_ICON = "heart-pulse";

/**
 * One follow up as a queue row, ledger-style: the ONE follow up fact leads
 * as a fixed gutter — the age count, days since the last Update, tinted by
 * its tier — then the Project as the chip it is everywhere else, its own
 * icon and its name, whole. The count wears no glyph here: the band names
 * the tier and the ink repeats it, and a heart on every row under "Stale"
 * would say the same thing a third time (the glyph earns its place where no
 * band carries the tier — a table cell, the board). The deadline rides the
 * count's tooltip ("Due in 5 hours"); the band orders the rows by it, so the
 * row itself spells neither tier nor date. The count is a value, not a
 * press: hover (or a held press on touch) reveals the action rail at the
 * row's end — Update, then Recent's Open in new tab and Copy link — and the
 * rail is the row's one verb. The row press opens the Project.
 *
 * The gutter leads rather than sitting between the icon and the name: the
 * icon and name are one identity unit (the seating Recent uses), and a
 * tinted number inside that unit's own gap reads as part of the name.
 *
 * The gutter is ONE column across every row: its width is the widest age
 * shape in the whole queue (`sizers`, folded bands included), not a fixed
 * number — every sizer renders invisible in the same grid cell as the count,
 * so the cell is as wide as the widest of them and the count right-aligns
 * inside it. The names start on one x, the units stack, and nothing shifts
 * when a band unfolds or a count grows a digit. No measuring, no magic
 * width: the font sizes the column.
 *
 * There is no place word (the Assigned to me rule) — a column of names is
 * what the card is for.
 */
export function FollowUpListRow({
  row,
  sizers,
  onOpen,
  onUpdate,
}: {
  row: FollowUpQueueRow;
  /** One string per shape of age in the queue — the column's width. */
  sizers: readonly string[];
  onOpen: () => void;
  onUpdate: () => void;
}) {
  const { activeWorkspace } = useWorkspace();
  // A Project the store holds without a workflow id has no shareable link.
  const href =
    activeWorkspace && row.issue.workflowId
      ? issueHref(activeWorkspace.urlPath, row.issue.workflowId, row.id)
      : undefined;

  // The count is a VALUE here — the rail's Update is the row's one verb, so
  // the count carries no press of its own (two Updates on a row read as two
  // different things). Its tooltip is the deadline the tint encodes.
  const gutter = (
    <Tooltip>
      <TooltipTrigger render={<Box display="grid" shrink={false} />}>
        {sizers.map((sizer) => (
          <Text key={sizer} aria-hidden className={SIZER_CELL} size="xs" tabular>
            {sizer}
          </Text>
        ))}
        <Box align="center" className={COUNT_CELL} direction="row">
          <AgeLabel age={row.issue.age} glyph={false} size="xs" stageId={row.issue.stageId} tabular />
        </Box>
      </TooltipTrigger>
      <TooltipPopup side="top">{row.deadline}</TooltipPopup>
    </Tooltip>
  );

  return (
    <WidgetListRow onClick={onOpen}>
      <Box align="center" direction="row" gap="sm" minWidth={0} shrink>
        {gutter}
        <Box minWidth={0} shrink>
          <EntityTag
            display="full"
            entity={{
              kind: "issue",
              id: row.id,
              issue: {
                name: row.name,
                icon: { icon: row.issue.icon?.icon, color: row.issue.icon?.color },
              },
              workflowId: row.issue.workflowId ?? undefined,
            }}
            interactive={false}
            weight="regular"
          />
        </Box>
      </Box>
      <Box align="center" className={RAIL_COLLAPSE} direction="row" shrink={false}>
        <RowLinkActions href={href}>
          <RowAction icon={UPDATE_ICON} label="Update" onClick={onUpdate} />
        </RowLinkActions>
      </Box>
    </WidgetListRow>
  );
}
